<template>
  <v-container fluid class="checkout-bg pa-0" style="min-height: 100vh;">
    <v-row justify="center" class="ma-0 py-10">
      <v-col cols="12" sm="9" md="6" lg="5">

        <!-- Checkout card -->
        <div class="checkout-card">

          <!-- Card header -->
          <div class="checkout-header">
            <div>
              <p class="checkout-header__tag">Pago parcial seguro</p>
              <h2 class="checkout-header__title">{{ planConcept }}</h2>
            </div>
            <div class="d-flex flex-column align-end">
              <v-img
                src="@/assets/images/weChamber/logo-WC-microsite.svg"
                max-width="30"
                contain
                class="checkout-header__logo mb-1"
              />
              <span class="checkout-header__secure">
                <v-icon x-small style="color:rgba(255,255,255,0.55)">mdi-lock</v-icon>
                <span>Pago seguro</span>
              </span>
            </div>
          </div>

          <!-- Step indicator -->
          <div v-if="step < 4" class="step-track">
            <div
              v-for="(label, i) in stepLabels"
              :key="i"
              class="step-item"
              :class="[
                step === i + 1 ? 'step-active' : step > i + 1 ? 'step-done' : 'step-idle',
                i < stepLabels.length - 1 ? 'step-item--has-connector' : '',
                step > i + 1 ? 'step-item--connector-done' : '',
              ]"
            >
              <div class="step-circle">
                <v-icon v-if="step > i + 1" x-small color="white">mdi-check</v-icon>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="step-label">{{ label }}</span>
            </div>
          </div>

          <!-- Card body -->
          <div class="checkout-body">

            <!-- Loading -->
            <div v-if="loading" class="text-center py-12">
              <v-progress-circular indeterminate size="44" color="#1b273e" width="3" />
              <p class="body-2 mt-4" style="color:#6b7280">Cargando información del plan...</p>
            </div>

            <!-- Error al cargar -->
            <v-alert v-else-if="loadError" type="error" outlined dense class="body-2">
              {{ loadError }}
            </v-alert>

            <template v-else>

              <!-- ── STEP 1: Resumen ── -->
              <div v-if="step === 1">
                <!-- Cuota activa -->
                <div class="installment-highlight mb-5">
                  <div class="installment-highlight__badge">
                    Cuota {{ nextInstallment ? nextInstallment.number : '—' }} de {{ paymentPlan ? paymentPlan.installmentsCount : '—' }}
                  </div>
                  <div class="d-flex justify-space-between align-end mt-3">
                    <div>
                      <p class="installment-highlight__label mb-0">Monto a pagar</p>
                      <p class="installment-highlight__amount mb-0">
                        ${{ formatAmount(nextInstallment ? nextInstallment.amount : 0) }}
                        <span class="installment-highlight__currency">MXN</span>
                      </p>
                    </div>
                    <div class="text-right">
                      <p class="installment-highlight__label mb-0">Fecha límite</p>
                      <p class="installment-highlight__date mb-0">{{ formatDate(nextInstallment ? nextInstallment.dueDate : null) }}</p>
                    </div>
                  </div>
                </div>

                <!-- Cronograma -->
                <p class="section-title mb-3">Cronograma de pagos</p>
                <div class="schedule-table">
                  <div
                    v-for="inst in paymentPlan ? paymentPlan.installments : []"
                    :key="inst.number"
                    class="schedule-row"
                    :class="{
                      'schedule-row--current': inst.number === (nextInstallment ? nextInstallment.number : 0),
                      'schedule-row--paid': inst.status === 'PAID',
                    }"
                  >
                    <div class="d-flex align-center">
                      <div class="schedule-status-dot" :class="'status-dot--' + inst.status.toLowerCase()">
                        <v-icon v-if="inst.status === 'PAID'" x-small color="white">mdi-check</v-icon>
                        <v-icon v-else-if="inst.status === 'OVERDUE'" x-small color="white">mdi-alert</v-icon>
                      </div>
                      <span class="schedule-row__label">Pago {{ inst.number }}</span>
                    </div>
                    <div class="d-flex align-center">
                      <span class="schedule-row__date">{{ formatDate(inst.dueDate) }}</span>
                      <span class="schedule-row__amount">${{ formatAmount(inst.amount) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Método de pago -->
                <p class="section-title mb-3 mt-6">Método de pago</p>
                <v-radio-group v-model="selectedPaymentMethod" class="mt-0 mb-2" hide-details>
                  <v-radio label="Tarjeta / OXXO / SPEI" value="STRIPE" color="#1b273e" />
                  <v-radio
                    v-if="showPeiboOption"
                    label="Peibo (Transferencia CLABE)"
                    value="PEIBO"
                    color="#1b273e"
                  />
                </v-radio-group>

                <!-- Peibo beneficiary inputs (only if no CLABE yet) -->
                <div v-if="selectedPaymentMethod === 'PEIBO' && !userHasPeiboClabe" class="peibo-fields mt-2">
                  <v-text-field
                    v-model="peiboData.beneficiary_name"
                    label="Nombre completo del beneficiario"
                    outlined
                    dense
                    rounded
                    prepend-inner-icon="mdi-account"
                  />
                  <v-text-field
                    v-model="peiboData.beneficiary_tax_id"
                    label="RFC del beneficiario"
                    outlined
                    dense
                    rounded
                    prepend-inner-icon="mdi-card-account-details"
                  />
                </div>

                <div class="step-actions step-actions--end mt-6">
                  <v-btn depressed class="btn-primary" :loading="preparingIntent" @click="goToStep2">
                    Continuar <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- ── STEP 2: Método de pago (Stripe) ── -->
              <div v-show="step === 2 && selectedPaymentMethod === 'STRIPE'">
                <p class="section-title mb-4">Selecciona tu método de pago</p>

                <!-- Stripe Payment Element (card + OXXO + SPEI) -->
                <div v-if="mountingElement" class="text-center py-6">
                  <v-progress-circular indeterminate size="32" color="#1b273e" width="2" />
                  <p class="caption mt-2" style="color:#6b7280">Preparando formulario de pago...</p>
                </div>
                <div
                  v-show="!mountingElement"
                  id="payment-element"
                  ref="paymentElementContainer"
                ></div>
                <p v-if="cardError" class="card-error-msg mt-2 mb-0">{{ cardError }}</p>

                <div class="step-actions mt-6">
                  <v-btn text class="btn-back" @click="step = 1">
                    <v-icon left small>mdi-arrow-left</v-icon> Regresar
                  </v-btn>
                  <v-btn depressed class="btn-primary" :disabled="!paymentElementReady" @click="step = 3">
                    Continuar <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- ── STEP 3: Confirmar (Stripe) ── -->
              <div v-if="step === 3 && selectedPaymentMethod === 'STRIPE'">
                <p class="section-title mb-4">Confirma tu pago</p>

                <div class="confirm-summary">
                  <div class="confirm-summary__row">
                    <span class="confirm-summary__label">Concepto</span>
                    <span class="confirm-summary__value">{{ planConcept }}</span>
                  </div>
                  <div class="confirm-summary__row">
                    <span class="confirm-summary__label">Cuota</span>
                    <span class="confirm-summary__value">
                      {{ nextInstallment ? nextInstallment.number : '—' }} de {{ paymentPlan ? paymentPlan.installmentsCount : '—' }}
                    </span>
                  </div>
                  <div class="confirm-summary__row confirm-summary__row--total">
                    <span class="confirm-summary__label">Total</span>
                    <span class="confirm-summary__total">
                      ${{ formatAmount(nextInstallment ? nextInstallment.amount : 0) }}
                      <span class="confirm-summary__currency">MXN</span>
                    </span>
                  </div>
                </div>

                <v-alert v-if="payError" type="error" outlined dense class="caption mt-3">{{ payError }}</v-alert>

                <div class="step-actions mt-6">
                  <v-btn text class="btn-back" :disabled="paying" @click="step = 2">
                    <v-icon left small>mdi-arrow-left</v-icon> Regresar
                  </v-btn>
                  <v-btn depressed class="btn-primary" :loading="paying" @click="pay">
                    <v-icon left small>mdi-lock-outline</v-icon>
                    Pagar ${{ formatAmount(nextInstallment ? nextInstallment.amount : 0) }} MXN
                  </v-btn>
                </div>
              </div>

              <!-- ── STEP 4: Resultado ── -->
              <div v-if="step === 4">
                <payment-result-screen
                  :success="paymentSuccess"
                  :is-reference="isReferencePayment"
                  :error-message="payError"
                  :installment-number="nextInstallment ? nextInstallment.number : 0"
                  :total-installments="paymentPlan ? paymentPlan.installmentsCount : 0"
                  :is-last-payment="isLastPayment"
                  :is-event="!!paymentPlan && !!paymentPlan.event"
                  :next-due-date="nextInstallmentAfter ? nextInstallmentAfter.dueDate : null"
                  :next-amount="nextInstallmentAfter ? nextInstallmentAfter.amount : 0"
                  @retry="resetToStep2"
                  @go-home="goHome"
                />
              </div>

            </template>
          </div>

          <!-- Card footer -->
          <div class="checkout-footer">
            <v-img
              src="@/assets/images/weChamber/poweredby.svg"
              max-width="90"
              contain
              class="mx-auto"
              style="opacity: 0.45;"
            />
          </div>

        </div>
      </v-col>
    </v-row>

    <!-- Peibo instructions dialog -->
    <v-dialog v-model="peiboDialog" max-width="600" persistent>
      <peibo-instructions
        :clabe="peiboClabe"
        :payment-concept="planConcept"
        :amount="'$' + formatAmount(nextInstallment ? nextInstallment.amount : 0) + ' MXN'"
      />
      <div class="text-center mt-4">
        <v-btn color="white" rounded class="font-weight-bold" @click="closePeiboDialog">
          Entendido
        </v-btn>
      </div>
    </v-dialog>
  </v-container>
</template>

<script>
import { colors } from '@/constants/colors';
import { mapState, mapActions } from 'vuex';
import PaymentResultScreen from '@/components/shared/checkout/PaymentResultScreen.vue';
import PeiboInstructions from '@/components/shared/PeiboInstructions.vue';

export default {
  name: 'PartialPaymentCheckout',

  components: {
    PaymentResultScreen,
    PeiboInstructions,
  },

  props: {
    paymentPlanId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      colors,
      step: 1,
      stepLabels: ['Resumen', 'Método', 'Confirmar'],
      loading: true,
      loadError: null,

      // Stripe account info
      stripeConnectId: null,
      stripeAccountType: null,

      // Stripe Payment Element
      stripeInstance: null,
      stripeElements: null,
      paymentElement: null,
      paymentElementReady: false,
      mountingElement: false,
      preparingIntent: false,
      clientSecret: null,
      cardError: null,

      // Payment state
      paying: false,
      payError: null,
      paymentSuccess: false,
      isReferencePayment: false,
      currentOrderId: null,
      refreshTimer: null,

      // Payment method selection
      selectedPaymentMethod: 'STRIPE',

      // Peibo state
      peiboData: { beneficiary_name: '', beneficiary_tax_id: '' },
      peiboClabe: '',
      peiboDialog: false,
    };
  },

  computed: {
    ...mapState('paymentPlan', ['currentPaymentPlan', 'nextInstallment']),
    ...mapState('user', ['user']),

    paymentPlan() {
      return this.currentPaymentPlan;
    },

    planConcept() {
      return this.paymentPlan?.orderConcept || 'Plan de pagos parciales';
    },

    showPeiboOption() {
      if (!this.paymentPlan || !this.paymentPlan.organization) return false;
      const orgId = this.paymentPlan.organization._id || this.paymentPlan.organization;
      return orgId === '696fe6b02268d27aba1d58e6';
    },

    userHasPeiboClabe() {
      return !!(this.user && this.user.peiboClabe);
    },

    isLastPayment() {
      if (!this.paymentPlan || !this.nextInstallment) return false;
      return (
        this.nextInstallment.number === this.paymentPlan.installmentsCount
      );
    },

    nextInstallmentAfter() {
      if (!this.paymentPlan || !this.nextInstallment) return null;
      const currentNum = this.nextInstallment.number;
      return (
        this.paymentPlan.installments.find(
          (i) => i.number === currentNum + 1 && i.status !== 'PAID',
        ) || null
      );
    },
  },

  async created() {
    await this.loadPlan();
  },

  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },

  methods: {
    ...mapActions('paymentPlan', [
      'fetchPaymentPlanDetail',
      'fetchNextInstallment',
      'processInstallmentPayment',
    ]),
    ...mapActions('user', ['realoadUserData']),
    ...mapActions('member', ['fetchMemberCredentials']),

    async loadPlan() {
      this.loading = true;
      this.loadError = null;
      try {
        const [planResult, methodsResult] = await Promise.all([
          this.fetchPaymentPlanDetail(this.paymentPlanId),
          this.$axios
            ? this.$axios
                .get(
                  `/payments/get-methods-payment/${
                    this.$store.state.user?.user?._id
                  }`,
                )
                .catch(() => null)
            : null,
        ]);

        await this.fetchNextInstallment(this.paymentPlanId);

        if (planResult?.status === false) {
          this.loadError = planResult.message || 'Error al cargar el plan de pagos.';
          return;
        }

        if (methodsResult?.data) {
          this.stripeConnectId = methodsResult.data.stripeConnectId || null;
        }
      } catch (e) {
        this.loadError = 'No se pudo cargar la información del plan de pagos.';
      } finally {
        this.loading = false;
      }
    },

    initStripe() {
      // Reset if account context changed
      this.stripeInstance = null;

      // STANDARD: intent lives on connected account → need stripeAccount
      // EXPRESS: intent lives on platform → do NOT use stripeAccount
      const useConnectedAccount =
        this.stripeAccountType === 'STANDARD' && this.stripeConnectId;

      if (useConnectedAccount) {
        this.stripeInstance = window.Stripe(process.env.VUE_APP_STRIPE_API_KEY, {
          stripeAccount: this.stripeConnectId,
        });
      } else {
        this.stripeInstance = window.Stripe(process.env.VUE_APP_STRIPE_API_KEY);
      }
    },

    mountPaymentElement() {
      // Destroy previous if any
      if (this.paymentElement) {
        this.paymentElement.destroy();
        this.paymentElement = null;
      }

      const container = this.$refs.paymentElementContainer;
      if (!container || !this.clientSecret) return;

      this.mountingElement = true;
      this.initStripe();

      this.stripeElements = this.stripeInstance.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#1b273e',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorDanger: '#dc2626',
            fontFamily: 'Roboto, Helvetica Neue, Helvetica, Arial, sans-serif',
            borderRadius: '8px',
          },
        },
        locale: 'es',
      });

      this.paymentElement = this.stripeElements.create('payment', {
        layout: 'tabs',
      });

      this.paymentElement.mount(container);

      this.paymentElement.on('ready', () => {
        this.mountingElement = false;
      });

      this.paymentElement.on('change', (event) => {
        this.paymentElementReady = event.complete;
        this.cardError = event.error ? event.error.message : null;
      });
    },

    async goToStep2() {
      // ── PEIBO FLOW ──
      if (this.selectedPaymentMethod === 'PEIBO') {
        this.preparingIntent = true;
        this.payError = null;
        try {
          const payload = {
            paymentPlanId: this.paymentPlanId,
            paymentType: 'PEIBO',
          };
          if (!this.userHasPeiboClabe) {
            if (!this.peiboData.beneficiary_name || !this.peiboData.beneficiary_tax_id) {
              this.payError = 'Ingresa el nombre y RFC del beneficiario.';
              this.preparingIntent = false;
              return;
            }
            payload.beneficiary_name = this.peiboData.beneficiary_name;
            payload.beneficiary_tax_id = this.peiboData.beneficiary_tax_id;
          }
          const result = await this.processInstallmentPayment(payload);
          if (result?.status === false) {
            this.payError = result.message || 'Error al procesar el pago con Peibo.';
            return;
          }
          this.peiboClabe = result.clabe;
          this.peiboDialog = true;
        } catch (e) {
          this.payError = e.message || 'Error al procesar el pago con Peibo.';
        } finally {
          this.preparingIntent = false;
        }
        return;
      }

      // ── STRIPE FLOW ──
      if (!this.clientSecret) {
        this.preparingIntent = true;
        try {
          const payload = {
            paymentPlanId: this.paymentPlanId,
            paymentType: 'CREDIT_CARD',
          };
          const result = await this.processInstallmentPayment(payload);

          if (result?.status === false) {
            this.payError = result.message || 'Error al preparar el pago.';
            this.preparingIntent = false;
            return;
          }

          this.clientSecret = result.paymentIntent.clientSecret;
          this.currentOrderId = result.orderId;
          this.stripeConnectId = result.stripeConnectId || null;
          this.stripeAccountType = result.stripeAccountType || null;
        } catch (e) {
          this.payError = e.message || 'Error al preparar el pago.';
          this.preparingIntent = false;
          return;
        } finally {
          this.preparingIntent = false;
        }
      }

      this.step = 2;

      if (this.clientSecret) {
        this.$nextTick(() => this.mountPaymentElement());
      }
    },

    async pay() {
      this.paying = true;
      this.payError = null;

      try {
        const returnUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:8080/3d-secure-output'
          : 'https://wechamber.mx/3d-secure-output';

        const { error, paymentIntent } = await this.stripeInstance.confirmPayment({
          elements: this.stripeElements,
          confirmParams: {
            return_url: returnUrl,
          },
          redirect: 'if_required',
        });

        if (error) {
          this.payError = error.message || 'El pago no pudo confirmarse.';
          this.step = 4;
          this.paymentSuccess = false;
          return;
        }

        this.paymentSuccess = true;
        this.isReferencePayment = !!(paymentIntent?.next_action?.oxxo_display_details || paymentIntent?.next_action?.display_bank_transfer_instructions);
        this.step = 4;

        // Detect OXXO/SPEI and send payment reference email
        if (paymentIntent?.next_action?.oxxo_display_details) {
          await this.sendPaymentReferenceEmail(
            paymentIntent.next_action.oxxo_display_details.hosted_voucher_url,
            'oxxo',
          );
        } else if (paymentIntent?.next_action?.display_bank_transfer_instructions) {
          await this.sendPaymentReferenceEmail(
            paymentIntent.next_action.display_bank_transfer_instructions.hosted_instructions_url,
            'spei',
          );
        }

        await this.fetchPaymentPlanDetail(this.paymentPlanId);
        this.refreshMemberData();
      } catch (e) {
        this.payError = e.message || 'Ocurrió un error inesperado.';
        this.step = 4;
        this.paymentSuccess = false;
      } finally {
        this.paying = false;
      }
    },

    resetToStep2() {
      this.payError = null;
      this.paymentSuccess = false;
      this.isReferencePayment = false;
      this.paymentElementReady = false;
      this.cardError = null;
      this.clientSecret = null;
      this.peiboClabe = '';
      this.peiboDialog = false;
      this.step = 1;
      // User goes back to step 1, will create a new intent on next goToStep2
    },

    closePeiboDialog() {
      this.peiboDialog = false;
      this.goHome();
    },

    async sendPaymentReferenceEmail(referenceLink, paymentType) {
      try {
        await this.axios({
          method: 'POST',
          url: '/payments/partial-payment/send-reference',
          data: {
            orderId: this.currentOrderId,
            referenceLink,
            paymentType,
          },
        });
      } catch (e) {
        console.error('Error sending payment reference email:', e);
      }
    },

    refreshMemberData() {
      this.refreshTimer = setTimeout(async () => {
        try {
          await Promise.all([
            this.realoadUserData({ email: this.user.email }),
            this.fetchMemberCredentials(),
          ]);
        } catch (e) {
          console.error('Error refreshing member data:', e);
        }
      }, 5000);
    },

    goHome() {
      this.$router.push({ name: 'Member Affiliation Profile' }).catch(() => {});
    },

    formatAmount(val) {
      if (!val && val !== 0) return '0.00';
      return Number(val).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleDateString('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },

    installmentIcon(status) {
      if (status === 'PAID') return 'mdi-check-circle';
      if (status === 'OVERDUE') return 'mdi-alert-circle';
      return 'mdi-clock-outline';
    },

    installmentIconColor(status) {
      if (status === 'PAID') return colors.primary;
      if (status === 'OVERDUE') return colors.contrastError;
      return colors.secondary;
    },
  },
};
</script>

<style scoped>
/* ── Background ─────────────────────────────────── */
.checkout-bg {
  background: #edf0f5;
}

/* ── Checkout card ───────────────────────────────── */
.checkout-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(27, 39, 62, 0.10), 0 1px 4px rgba(27, 39, 62, 0.06);
  overflow: hidden;
}

/* ── Card header ─────────────────────────────────── */
.checkout-header {
  background: #1b273e;
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.checkout-header__tag {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 5px 0;
}
.checkout-header__title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
}
.checkout-header__logo {
  filter: brightness(0) invert(1);
  opacity: 0.9;
}
.checkout-header__secure {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

/* ── Step track ──────────────────────────────────── */
.step-track {
  display: flex;
  align-items: center;
  padding: 14px 28px;
  background: #f8f9fc;
  border-bottom: 1px solid #e8ecf0;
}
.step-item {
  display: flex;
  align-items: center;
  gap: 7px;
}
.step-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.step-idle .step-circle {
  background: #e5e8ee;
  color: #9ea8b5;
}
.step-active .step-circle {
  background: #1b273e;
  color: #ffffff;
}
.step-done .step-circle {
  background: #1b273e;
  color: #ffffff;
}
.step-label {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.step-idle .step-label  { color: #9ea8b5; }
.step-active .step-label { color: #1b273e; font-weight: 700; }
.step-done .step-label  { color: #4a5568; }
.step-item--has-connector {
  flex: 1;
}
.step-item--has-connector::after {
  content: '';
  display: block;
  height: 1px;
  background: #e5e8ee;
  margin: 0 8px;
  flex: 1;
}
.step-item--has-connector.step-item--connector-done::after {
  background: #1b273e;
}

/* ── Card body ───────────────────────────────────── */
.checkout-body {
  padding: 28px;
}

/* ── Section titles ──────────────────────────────── */
.section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin: 0;
}

/* ── Installment highlight ───────────────────────── */
.installment-highlight {
  background: #f0f4f9;
  border: 1px solid #d4dce8;
  border-left: 4px solid #1b273e;
  border-radius: 0 10px 10px 0;
  padding: 18px 20px;
}
.installment-highlight__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #1b273e;
  background: rgba(27, 39, 62, 0.09);
  border-radius: 4px;
  padding: 2px 8px;
}
.installment-highlight__label {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.installment-highlight__amount {
  font-size: 28px;
  font-weight: 800;
  color: #1b273e;
  line-height: 1.1;
}
.installment-highlight__currency {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}
.installment-highlight__date {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

/* ── Schedule table ──────────────────────────────── */
.schedule-table {
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
}
.schedule-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.schedule-row:last-child {
  border-bottom: none;
}
.schedule-row--current {
  background: #f0f4f9;
}
.schedule-row--paid {
  opacity: 0.6;
}
.schedule-status-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}
.status-dot--paid    { background: #1b273e; }
.status-dot--pending { background: #d1d5db; }
.status-dot--overdue { background: #dc2626; }
.schedule-row__label {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}
.schedule-row__date {
  font-size: 12px;
  color: #6b7280;
  margin-right: 16px;
}
.schedule-row__amount {
  font-size: 13px;
  font-weight: 600;
  color: #1b273e;
  min-width: 70px;
  text-align: right;
}

/* ── Confirm summary ─────────────────────────────── */
.confirm-summary {
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
}
.confirm-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.confirm-summary__row:last-child {
  border-bottom: none;
}
.confirm-summary__row--total {
  background: #f0f4f9;
}
.confirm-summary__label {
  font-size: 13px;
  color: #6b7280;
}
.confirm-summary__value {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
}
.confirm-summary__total {
  font-size: 22px;
  font-weight: 800;
  color: #1b273e;
}
.confirm-summary__currency {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
}

/* ── Info note ───────────────────────────────────── */
.info-note {
  display: flex;
  align-items: flex-start;
  background: #f8f9fc;
  border: 1px solid #e8ecf0;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 12px;
  color: #4b5563;
  line-height: 1.55;
}

/* ── SPEI box ────────────────────────────────────── */
.spei-box {
  border: 1px solid #d4dce8;
  border-left: 4px solid #1b273e;
  border-radius: 0 10px 10px 0;
  background: #f0f4f9;
  padding: 16px 20px;
}
.spei-box__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  margin: 0 0 6px 0;
}
.spei-box__clabe {
  font-size: 20px;
  font-weight: 800;
  color: #1b273e;
  letter-spacing: 0.04em;
  margin: 0 0 6px 0;
  word-break: break-all;
}
.spei-box__note {
  font-size: 11px;
  color: #9ca3af;
  margin: 0;
}

/* ── Action buttons ──────────────────────────────── */
.step-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}
.step-actions--end {
  justify-content: flex-end;
}
.btn-primary {
  background: #1b273e !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.03em !important;
  border-radius: 8px !important;
  padding: 0 22px !important;
  height: 40px !important;
}
.btn-back {
  color: #6b7280 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
}

/* ── Card footer ─────────────────────────────────── */
.checkout-footer {
  padding: 16px 28px 20px;
  border-top: 1px solid #f0f2f5;
  background: #f8f9fc;
}

/* ── Stripe Payment Element ───────────────────────── */
#payment-element {
  min-height: 100px;
}
.card-error-msg {
  font-size: 12px;
  color: #dc2626;
}
</style>
